/**
 * Modal de scan intégré (code-barres 1D + QR).
 *
 * Utilise html5-qrcode (déjà installé) avec Html5Qrcode standalone :
 *   - Caméra arrière prioritaire (facingMode: environment)
 *   - Support automatique CODE_128 / EAN / QR / DATA_MATRIX
 *   - Torche activable si supportée
 *
 * On ne charge la lib qu'à l'ouverture (import dynamique) pour ne pas
 * grossir le bundle initial.
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Zap, ZapOff, Camera, AlertCircle } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  onDetected: (raw: string) => void;
}

const READER_ID = 'verif-scanner-region';

export default function ScannerModal({ open, onClose, onDetected }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const scannerRef = useRef<any>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let stop = () => Promise.resolve();

    (async () => {
      try {
        const mod = await import('html5-qrcode');
        if (cancelled) return;
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = mod as any;

        const scanner = new Html5Qrcode(READER_ID, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.DATA_MATRIX,
          ],
          verbose: false,
        });
        scannerRef.current = scanner;

        const config = {
          fps: 15,
          qrbox: (w: number, h: number) => {
            const side = Math.min(w, h) * 0.75;
            return { width: side, height: side };
          },
          aspectRatio: 1.0,
        };

        await scanner.start(
          { facingMode: 'environment' },
          config,
          (decoded: string) => {
            // Un seul callback : on stoppe puis notifie
            try { scanner.pause(true); } catch { /* ignore */ }
            onDetected(decoded);
          },
          () => { /* silence les erreurs de scan par frame */ },
        );

        // Torche : uniquement dispo si le navigateur expose le track
        try {
          const state = scanner.getState();
          const el = document.getElementById(READER_ID);
          const video = el?.querySelector('video') as HTMLVideoElement | null;
          const stream = video?.srcObject as MediaStream | undefined;
          const track = stream?.getVideoTracks?.()[0];
          if (track) {
            trackRef.current = track;
            const caps: any = track.getCapabilities?.() || {};
            if (caps.torch) setTorchAvailable(true);
          }
          void state;
        } catch { /* ignore */ }

        stop = async () => {
          try {
            await scanner.stop();
            await scanner.clear();
          } catch { /* ignore */ }
        };
      } catch (err: any) {
        console.error('Scanner error:', err);
        setError(
          err?.message?.includes('Permission')
            ? 'Accès caméra refusé. Vérifie les permissions du navigateur.'
            : 'Impossible d\'initialiser le scanner. Ferme et réessaie.',
        );
      }
    })();

    return () => {
      cancelled = true;
      stop();
      trackRef.current = null;
      setTorchOn(false);
      setTorchAvailable(false);
    };
  }, [open, onDetected]);

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn } as any] });
      setTorchOn(t => !t);
    } catch { /* ignore */ }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex items-center justify-between p-3 bg-black/80 text-white">
        <div className="flex items-center gap-2">
          <Camera size={20} />
          <span className="font-semibold text-sm">Scanner carte ou reçu</span>
        </div>
        <div className="flex gap-2">
          {torchAvailable && (
            <button
              type="button"
              onClick={toggleTorch}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20"
              aria-label="Torche"
            >
              {torchOn ? <ZapOff size={18} /> : <Zap size={18} />}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center relative">
        <div id={READER_ID} className="w-full max-w-md aspect-square bg-black" />
        {error && (
          <div className="absolute inset-x-4 top-8 bg-red-600 text-white p-3 rounded-lg text-sm flex items-start gap-2">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="p-4 bg-black/80 text-white/80 text-xs text-center space-y-1">
        <p>Cadrez le <b>code-barres</b> de la carte d'étudiant ou le <b>QR code</b> du reçu.</p>
        <p className="text-white/60">
          Reste immobile 1 à 2 secondes. Bon éclairage recommandé.
        </p>
      </div>
    </div>
  );
}
